import { z } from "zod";
import { moneyText } from "../money";

/**
 * What has expired, and what is about to.
 *
 * Four groups: already expired, then within one, two and three months. Three
 * months because that is when a pharmacy has to start the conversation —
 * return it to the supplier, sell it first, discount it — before it becomes a
 * loss.
 *
 * The three windows are CUMULATIVE, as the owner asked: something expiring in
 * twenty days is within one month, and so also within two months and within
 * three. "Within 3 months" is everything coming up; "within 1 month" is the
 * most urgent part of it. Expired stands apart — it is past its date, not
 * coming up — and is counted in none of the windows.
 *
 * This is also the daily reminder. The owner chose it over a phone
 * notification or an SMS: it is on the Home screen, which is the first thing
 * anybody sees when they open the app.
 */

export const expiryGroup = z.enum(["expired", "month1", "month2", "month3"]);

export const expiryGroupSummary = z.object({
  group: expiryGroup,
  /** Distinct medicines, which is what the Home box counts. */
  medicineCount: z.number().int(),
  batchCount: z.number().int(),
  /**
   * What the stock in this group cost the shop — what is at risk of being
   * lost. Null for anybody not allowed to see cost prices.
   */
  value: moneyText.nullable(),
});

export const expiringBatch = z.object({
  batchId: z.string().uuid(),
  productId: z.string().uuid(),
  productName: z.string(),
  strength: z.string().nullable(),
  baseUnit: z.string(),
  expiryDate: z.string(),
  batchNo: z.string().nullable(),
  qtyLeftBase: z.number().int(),
  /**
   * Every group this batch is in: ["expired"] on its own, or a run of windows —
   * ["month1", "month2", "month3"] for something twenty days away,
   * ["month3"] for something eighty days away.
   */
  groups: z.array(expiryGroup),
  /** Null for anybody not allowed to see cost prices. */
  value: moneyText.nullable(),
});

export const expiryResponse = z.object({
  /** The pharmacy's own today, which every group is measured from. */
  today: z.string(),
  /** Always all four, in order, even when a group is empty. */
  groups: z.array(expiryGroupSummary),
  /** Soonest expiry first. */
  batches: z.array(expiringBatch),
});

export type ExpiryGroup = z.infer<typeof expiryGroup>;
export type ExpiryGroupSummary = z.infer<typeof expiryGroupSummary>;
export type ExpiringBatch = z.infer<typeof expiringBatch>;
export type ExpiryResponse = z.infer<typeof expiryResponse>;
